import { useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { dialAtPointer, VolumeChannel } from '~/modules/elements/volume-dials';
import VolumeOverlay from '~/modules/elements/volume-overlay';
import {
  EffectsVolumeSetting,
  MasterVolumeSetting,
  MusicVolumeSetting,
  useSettingValue,
} from '~/routes/settings/settings-state';

/** Percentage points per notch of the wheel, or per keypress. */
const STEP = 5;

/** How long the dials stay up after the last adjustment. */
const VISIBLE_MS = 2_000;

const clamp = (value: number) => Math.max(0, Math.min(100, value));

const SETTING_OF_CHANNEL = {
  master: MasterVolumeSetting,
  music: MusicVolumeSetting,
  effect: EffectsVolumeSetting,
};

/**
 * Volume, on Alt.
 *
 * A bare wheel used to do this, deferring to anything under the pointer that looked scrollable. The
 * trouble is that a screen can have somewhere to scroll without the pointer being over it - the
 * song browser keeps its filters in a fixed strip above a scrolling list, and a wheel over that
 * strip belongs to the list below it, not to the volume. Every rule of that shape leaves holes like
 * it, and a hole is invisible until somebody scrolls into one and the music jumps.
 *
 * So the wheel is left alone entirely and the volume asks to be meant: hold Alt and scroll, or
 * press Alt with plus or minus. Ctrl is the browser's zoom and Shift is its horizontal scroll, so
 * neither is free.
 */
export default function VolumeScroll() {
  // Only for rendering the dials. Adjusting goes to the settings themselves, which is why the
  // listener can be bound once: it never reads a value captured in a closure, so a burst of wheel
  // events in one frame each step from the previous one instead of all collapsing into a single
  // step off a stale base.
  const [master] = useSettingValue(MasterVolumeSetting);
  const [music] = useSettingValue(MusicVolumeSetting);
  const [effect] = useSettingValue(EffectsVolumeSetting);
  const [active, setActive] = useState<VolumeChannel | null>(null);

  const adjust = (channel: VolumeChannel, direction: 1 | -1) => {
    const setting = SETTING_OF_CHANNEL[channel];

    setting.set(clamp(setting.get() + direction * STEP));
    setActive(channel);
  };

  useEffect(() => {
    const onWheel = (event: WheelEvent) => {
      if (!event.altKey) return;

      // Alt+wheel scrolls in some browsers, and adjusting the volume should not also move the page.
      event.preventDefault();

      const channel = dialAtPointer(event.clientX, event.clientY, {
        width: window.innerWidth,
        height: window.innerHeight,
      });

      adjust(channel, event.deltaY < 0 ? 1 : -1);
    };

    // Not passive: preventDefault is the whole point.
    window.addEventListener('wheel', onWheel, { passive: false });

    return () => window.removeEventListener('wheel', onWheel);
  }, []);

  // The keyboard is the primary way around this game, so the volume needs a key of its own rather
  // than only a mouse gesture. Master only - the per-channel dials are a pointer affordance.
  useHotkeys('alt+=, alt+shift+=', () => adjust('master', 1));
  useHotkeys('alt+-', () => adjust('master', -1));

  // Restarted by each adjustment, so a run of them keeps the dials up throughout.
  useEffect(() => {
    if (active === null) return;

    const timeout = setTimeout(() => setActive(null), VISIBLE_MS);

    return () => clearTimeout(timeout);
  }, [active, master, music, effect]);

  // Kept mounted so the dials can fade out rather than vanishing.
  return <VolumeOverlay volumes={{ master, music, effect }} active={active ?? 'master'} visible={active !== null} />;
}
